import React, { useEffect, useState } from "react";
import { Button, Input, Modal, Popover, Table } from "antd";
import { DeleteOutlined, DollarOutlined, ExclamationCircleOutlined, FileImageOutlined } from "@ant-design/icons";

import useGlobal from "../../../store";
import useTable from "../../hooks/useTable";
import Services from "../../../services";
import Toaster from "../../../services/toaster";
import { generateQueryString } from "../../search/generateQueryString";
import Helpers from "../../../services/helpers";
import { useUserContext } from "../../contexts/usercontext";
import useFirstMountState from "../../hooks/useFirstMountState";
import { ERRORS } from "../../../globals";
import { ENTITY_TYPES } from "utils/constants";
import moment from "moment";

const getColumns = (
  showSellerInformation,
  showBuyerInformation,
  user,
  showModal,
  setFlaggedData,
  getFlaggedData,
  getBestOfferPrice,
  setFileImage,
  getBuyerFeedback,
  buyerFeedback,
  identifier,
  resultIdentifierCC,
  entityType,
  clickSelectItem,
) => {
  const rows = [
    {
      title: "Image",
      key: "imageUrl",
      width: 100,
      render: (text, record) => {
        let popContent;
        let imgSrc = "/no-image-2.jpg";
        if (record.source === "ebay_jobs" || entityType === ENTITY_TYPES.SEALED_WAX) {
          popContent = (
            <div>
              <strong>ID:</strong> {record.key}
              <br />
              <strong>eBay ID:</strong> {record.auction_id}
            </div>
          );
          imgSrc = record.imageUrl;
        } else if (record.source === "myslabs_jobs") {
          popContent = (
            <div>
              <strong>ID:</strong> {record.key}
              <br />
              <strong>My Slabs ID:</strong> {record.auction_id.replace("myslabs-", "")}
            </div>
          );
          imgSrc = record.imageUrl;
        }

        const image = <img width={75} alt={record.title} src={imgSrc} />;

        return popContent ? (
          <Popover placement="top" content={popContent} trigger="hover">
            {image}
          </Popover>
        ) : (
          image
        );
      },
    },
    {
      title: "Sale Day/Time",
      dataIndex: "end_time",
      key: "end_time",
      width: 115,
      render: (text, record) => (
        <>
          <span className="hidden-desktop inline-table-label">Sale Day/Time: </span>
          {Helpers.formatDateWithTimeTZ(record.end_time)}
        </>
      ),
      sorter: () => null,
      defaultSortOrder: "descend",
    },
    {
      title: "Listing Title",
      dataIndex: "title",
      key: "title",
      width: 300,
      render: (text, record) => {
        let link;
        if (record.source === "ebay_jobs" || entityType === ENTITY_TYPES.SEALED_WAX) {
          link = Helpers.generatePartnerLinkFromAuctionURL(record.auction_url);
        } else if (record.source === "myslabs_jobs") {
          link = Helpers.generatePartnerLinkFromAuctionId(record.auction_id, record.source);
        }

        return (
          <>
            <span className="hidden-desktop inline-table-label">Listing Title: </span>
            {link ? (
              <a href={link} target="_blank" rel="noreferrer">
                {record.title}
              </a>
            ) : (
              <span>{record.title}</span>
            )}
          </>
        );
      },
    },
    showBuyerInformation
      ? {
          title: "Buyer Feedback",
          dataIndex: "buyer_feedback_rating",
          key: "buyer_feedback_rating",
          width: 115,
          render: (text, record) =>
            record.source === "ebay_jobs" || entityType === ENTITY_TYPES.SEALED_WAX ? (
              <>
                <span className="hidden-desktop inline-table-label">Listing Buyer: </span>
                {record.buyer_feedback_rating || buyerFeedback[record.auction_id] ? (
                  Helpers.parseBuyerFeedbackRating(record.buyer_feedback_rating || buyerFeedback[record.auction_id])
                ) : user.is_admin && moment.duration(moment().diff(record.end_time)).asDays() <= 30 ? (
                  <Button onClick={() => getBuyerFeedback(record.auction_id)}>Get Rating</Button>
                ) : null}
              </>
            ) : (
              <div style={{ textAlign: "center" }}>-</div>
            ),
        }
      : {},
    {
      title: "Seller Feedback",
      dataIndex: "seller_feedback_rating",
      key: "seller_feedback_rating",
      width: 115,
    },
    showSellerInformation
      ? {
          title: "Seller",
          dataIndex: "seller_name",
          key: "seller_name",
          width: 115,
          align: "center",
          render: (text, record) => Helpers.getPlatformLogo(record, true),
        }
      : {},
    {
      title: "Sale Type",
      dataIndex: "listing_type",
      key: "listing_type",
      width: 115,
      sorter: () => null,
      // The things you can pull with string templates...
      render: (text, record) => (
        <>
          <span className="hidden-desktop inline-table-label">Sale Type: </span>
          {Helpers.calculateResultType(record)}
          {!!record.number_of_bids && ` (${record.number_of_bids} ${record.number_of_bids > 1 ? "Bids" : "Bid"})`}
        </>
      ),
      filters: Object.keys(Helpers.formattedAuctionTypes).map((at) => {
        return {
          text: at,
          value: Helpers.formattedAuctionTypes[at],
        };
      }),
    },
    {
      title: "Selling Price",
      dataIndex: "selling_price",
      key: "selling_price",
      width: 115,
      render: (text, record) => (
        <>
          <span className="hidden-desktop inline-table-label">Selling Price: </span>
          {Helpers.formatMoney(record.selling_price)}
        </>
      ),
      sorter: () => null,
    },
    {
      title: "Offer Accepted",
      dataIndex: "offer_price",
      key: "offer_price",
      width: 120,
      render: (text, record) => {
        if (record.source === "ebay_jobs" || entityType === ENTITY_TYPES.SEALED_WAX) {
          if (record.offer_price > 0) {
            return (
              <>
                <span className="hidden-desktop inline-table-label">Offer Accepted: </span>
                {Helpers.formatMoney(record.offer_price)} {record.offer_price_message}
              </>
            );
          } else {
            return (
              <>
                <span className="hidden-desktop inline-table-label">Offer Accepted: </span>
                N/A {record.offer_price_message}
              </>
            );
          }
        } else {
          return <div style={{ textAlign: "center" }}>-</div>;
        }
      },
    },
    user
      ? {
          title: "Remove & Report",
          key: "flag",
          width: 100,
          render: (text, record) => (
            <Button.Group>
              <Button
                type={"warning"}
                onClick={() => {
                  setFlaggedData(record);
                  showModal();
                }}
              >
                <ExclamationCircleOutlined />
              </Button>
            </Button.Group>
          ),
        }
      : {},
  ];

  if (user?.is_admin) {
    rows.unshift({
      title: "Admin",
      key: "admin",
      width: 10,
      render: (text, record) => (
        <Button.Group style={{ flexDirection: "column" }}>
          <Button type={"danger"} onClick={() => clickSelectItem(record.key)}>
            <DeleteOutlined />
          </Button>

          <Button
            onClick={() => {
              Modal.confirm({
                title: "Are you sure you want to set this as the ADMIN photo?",
                okText: "Yes",
                okType: "danger",
                cancelText: "No",
                onOk() {
                  setFileImage(record[identifier], record.auction_id);
                },
              });
            }}
          >
            <FileImageOutlined />
          </Button>
          <Button
            type={"primary"}
            onClick={() => {
              Modal.confirm({
                title: "DOUBLE CHECK BEST OFFER PRICE, ARE YOU SURE?",
                okText: "Yes",
                okType: "danger",
                cancelText: "No",
                onOk() {
                  getBestOfferPrice(record.key, record.selling_price, record[identifier]);
                },
              });
            }}
          >
            <DollarOutlined />
          </Button>
        </Button.Group>
      ),
    });
  }

  return rows;
};

const PopularDetailedResultsPanel = ({
  entity,
  entityType = ENTITY_TYPES.CARD,
  results,
  onPaginationChange,
  onImageChange,
  selectedItems = [],
  clickSelectItem,
}) => {
  const { user } = useUserContext();
  const [flaggedData, setFlaggedData] = useState({});
  const [visible, setVisible] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [globalState] = useGlobal();
  const isFirstMount = useFirstMountState();
  const [buyerFeedback, setBuyerFeedback] = useState({});

  const { tableProps, pageSize, currentPage, sortBy, filters } = useTable({
    defaultPageSize: user.is_admin ? 50 : 10,
    totalCount: results ? results.total : 0,
    initialSort: {
      sortBy: "end_time",
      orderBy: "desc",
    },
  });

  const [name, identifier, resultIdentifier, resultIdentifierCC, setImageFn, bestOfferFn] = {
    [ENTITY_TYPES.CARD]: [
      "Card",
      "card_id",
      "card_result_id",
      "cardResultId",
      "setCardImage",
      "getCardResultBestOffer",
    ],
    [ENTITY_TYPES.SEALED_WAX]: [
      "Wax",
      "sealed_wax_id",
      "sealed_wax_result_id",
      "sealedWaxResultId",
      "setSealedWaxImage",
      "getSealedWaxResultBestOffer",
    ],
  }[entityType];

  const getBuyerFeedback = async (id) => {
    const { buyer_feedback_rating } = await Services.getBuyerFeedback(id, entityType);

    if (buyer_feedback_rating && !isNaN(parseFloat(buyer_feedback_rating))) {
      setBuyerFeedback({ ...buyerFeedback, [id]: buyer_feedback_rating });
    } else {
      setBuyerFeedback({ ...buyerFeedback, [id]: ERRORS.BUYER_UNAVAILABLE });
    }
  };

  useEffect(() => {
    if (!isFirstMount) {
      onPaginationChange(entity.id, { limit: pageSize, skip: currentPage - 1 }, sortBy, filters);
    }
  }, [pageSize, currentPage, sortBy, filters]);

  const showModal = () => {
    setVisible(true);
  };

  const hideModal = () => {
    setVisible(false);
    setReportReason("");
  };

  const saveFlaggedData = async () => {
    try {
      const data = {
        [resultIdentifier]: flaggedData.key,
        reason: reportReason,
        search_string: generateQueryString(flaggedData, false),
      };
      await Services.createFlaggedCard(data);
      onPaginationChange(entity.id, { limit: pageSize, skip: currentPage - 1 }, sortBy, filters);
      Toaster.SuccessNotification(`Successfully reported ${name.toLowerCase()}.`);
    } catch (e) {
      Toaster.ErrorNotification(`Error occurred reporting ${name.toLowerCase()}.`);
    }
    hideModal();
  };

  const setFileImage = (entityId, auctionId) => {
    try {
      Services[setImageFn](entityId, auctionId).then(({ card_id, s3Save }) => {
        if (onImageChange) {
          onImageChange({
            id: Number(card_id),
            image_url: s3Save,
          });
        }

        Toaster.SuccessNotification(`${name} has been updated.`, "");
      });
    } catch (e) {
      Toaster.ErrorNotification("Error occurred setting image", e.message);
    }
  };

  const getBestOfferPrice = async (resultId, selling_price, card_id) => {
    const bestOfferResponse = await Services[bestOfferFn](resultId);
    if (bestOfferResponse.errorMessage) {
      // WHAT DO WE DO?
      if (bestOfferResponse.errorMessage === "no results") {
        Toaster.SuccessNotification("Price Updated", "Offer Price updated: $" + selling_price);
        onPaginationChange(card_id, { limit: pageSize, skip: currentPage - 1 }, sortBy, filters);
      } else {
        Toaster.WarningNotification("Error Updating Price", bestOfferResponse.errorMessage);
        onPaginationChange(card_id, { limit: pageSize, skip: currentPage - 1 }, sortBy, filters);
        return;
      }
    }
    if (bestOfferResponse.currency && bestOfferResponse.currency !== "USD") {
      // WHAT DO WE DO?
      Toaster.WarningNotification("Foreign Currency", "Offer price is not in USD, keeping excluded.");
      onPaginationChange(card_id, { limit: pageSize, skip: currentPage - 1 }, sortBy, filters);
      return;
    }
    if (bestOfferResponse.best_offer_price > 0) {
      Toaster.SuccessNotification("Price Updated", "Offer Price updated: $" + bestOfferResponse.best_offer_price);
      onPaginationChange(card_id, { limit: pageSize, skip: currentPage - 1 }, sortBy, filters);
    }
  };

  const getFlaggedData = (entityId, terms) => {
    const search_string = generateQueryString(terms, false);
    return {
      user_id: user.id,
      [identifier]: entityId,
      search_string,
      // reason: ''
    };
  };

  const handleReasonChange = (event) => {
    setReportReason(event.target.value);
  };

  const data = results
    ? results.data.map((result) => ({
        [identifier]: entity.id,
        key: result.id,
        auction_id: result.auction_id,
        title: result.title,
        end_time: result.end_time,
        imageUrl: result.image_url,
        auction_url: result.auction_url,
        selling_price: result.selling_price,
        offer_price: result.offer_price,
        listing_type: result.listing_type,
        offer_price_message: result.offer_price_message,
        number_of_bids: result.number_of_bids,
        seller_name: result.seller_name,
        buyer_feedback_rating: result.buyer_feedback_rating,
        seller_feedback_rating: result.feedback_rating,
        source: result.source,
      }))
    : [];

  return (
    <>
      <Table
        className="ant-table-mobile"
        rowClassName={(record) => selectedItems.includes(record.key) && "table-row-selected"}
        columns={getColumns(
          globalState.showSellerInformation,
          globalState.showBuyerInformation,
          user,
          showModal,
          setFlaggedData,
          getFlaggedData,
          getBestOfferPrice,
          setFileImage,
          getBuyerFeedback,
          buyerFeedback,
          identifier,
          resultIdentifierCC,
          entityType,
          clickSelectItem,
        )}
        dataSource={data}
        scroll={{ y: "80vh", x: true }}
        {...tableProps}
      />

      <Modal
        width={800}
        title="Remove & Report"
        visible={visible}
        onOk={saveFlaggedData}
        okText="Yes, Remove"
        onCancel={hideModal}
      >
        <p>Are you sure this {name} does not match the search?</p>
        <div>Clicking yes will temporarily remove this {name} and report it to the admin for permanent removal.</div>
        <div>
          Reason: <Input type={"text"} value={reportReason} onChange={handleReasonChange} />
        </div>
      </Modal>
    </>
  );
};

export default PopularDetailedResultsPanel;
